import styled, { createGlobalStyle } from 'styled-components'
import WizrFlexLayout from '../WizrFlexLayout'
import WizrText from '../WizrText'
import WizrDropDown from '../WizrDropDown'
import { useEffect, useRef, useState } from 'react'
import { useGetIntegrationList, useGetMasterIntegrationList } from '../../redux/selectors/content'
import {
  dispatchGetIntegrationForTools,
  dispatchGetIntegrationToolsForTenants,
  dispatchImportPastTickets,
  dispatchMasterIntegrationList,
} from '../../redux/actions/content'
import ZendeskIcon from '../../assets/icons/ZendeskIcon'
import { DatePicker, Modal } from 'antd'
import WizrButton from '../WizrButton'
import dayjs, { Dayjs } from 'dayjs'
import Freshdesk from '../../assets/icons/FreshdeskIcon'
import Salesforce from '../../assets/icons/Salesforce'
import JiraIcon from '../../assets/icons/Jira'
import SharepointIcon from '../../assets/icons/sharepoint'
import ServiceNow from '../../assets/icons/ServiceNowIcon'
import ConfluenceIcon from '../../assets/icons/confluence'
import Slack from '../../assets/icons/Slack'
import PostgreSQLIcon from '../../assets/icons/PostgreSQLIcon'
import Gemini from '../../icons/Gemini'
import Pinecone from '../../assets/icons/Pinecone'
import DatabaseIcon from '../../assets/icons/DatabaseIcon'
import SMTPIcon from '../../assets/icons/SMTPIcon'
import SendEmailIcon from '../../assets/icons/SendEmailIcon'
import MongoDBIcon from '../../assets/icons/MongoDbIcon'

const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  background-color: #fff;
`

const DateFieldContainer = styled(InputContainer)`
  flex: 1;
  min-width: 0;
  width: 100%;
`

const StyledDropDown = styled(WizrDropDown)`
  height: 50px;
  .ant-select-selector {
    border-radius: 16px;
    border: 1px solid #d9d9d9 !important;
    height: 50px !important;
    background-color: #f2f4f9 !important;
    display: flex !important;
    align-items: center !important;
    .ant-select-selection-placeholder {
      display: flex;
      align-items: center;
    }
    .ant-select-selection-item {
      display: flex !important;
      align-items: center !important;
    }
  }

  &.ant-select-focused .ant-select-selector,
  &.ant-select-open .ant-select-selector {
    border-color: #000 !important;
    box-shadow: none !important;
  }
`

const pickerFieldStyles = `
  width: 100% !important;

  &.ant-picker {
    background-color: #f2f4f9 !important;
    border-radius: 16px !important;
    height: 50px !important;
    width: 100% !important;
    border: 1px solid #d9d9d9 !important;
    padding: 4px 12px !important;
    align-items: center;
    box-shadow: none !important;
  }

  &:hover.ant-picker {
    border-color: #d9d9d9 !important;
  }

  &.ant-picker-focused {
    border-color: #000 !important;
    box-shadow: none !important;
    outline: none !important;
  }

  .ant-picker-input {
    height: 100%;
    align-items: center;
  }

  .ant-picker-input > input {
    font-size: 14px;
    height: 100%;
    line-height: normal;
  }

  .ant-picker-suffix {
    color: #999b9f;
  }
`

const StyledDatePicker = styled(DatePicker)`
  ${pickerFieldStyles}
`

const InlineDateTimeHost = styled.div`
  position: relative;
  width: 100%;
  min-height: min(340px, calc(100vh - 220px));
  max-height: calc(100vh - 220px);
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 4px 0;
`

const ImportDatePickerDropdownStyles = createGlobalStyle`
  .wizr-import-date-inline-host .wizr-import-date-popup.ant-picker-dropdown {
    position: relative !important;
    top: 0 !important;
    left: 0 !important;
    right: auto !important;
    bottom: auto !important;
    inset: auto !important;
    transform: none !important;
    box-shadow: none;
    z-index: 1;
  }

  .wizr-import-date-inline-host .ant-picker {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
    overflow: hidden;
    padding: 0;
    margin: 0;
    border: none;
  }

  .wizr-import-date-inline-host .ant-picker-panel-container {
    border-radius: 16px;
    box-shadow: none;
  }

  .wizr-import-date-inline-host .ant-picker-datetime-panel {
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    max-height: calc(100vh - 260px);
    overflow: auto;
  }

  .wizr-import-date-inline-host .ant-picker-time-panel-column {
    max-height: min(220px, calc(100vh - 320px));
    overflow-y: auto;
  }
`

type ActivePicker = 'start' | 'end' | null

interface DateTimePickerModalProps {
  open: boolean
  title: string
  value: Dayjs | null
  disabledDate?: (date: Dayjs) => boolean
  onConfirm: (date: unknown, dateString: string | string[]) => void
  onClose: () => void
}

const DateTimePickerModal = ({
  open,
  title,
  value,
  disabledDate,
  onConfirm,
  onClose,
}: DateTimePickerModalProps) => {
  const hostRef = useRef<HTMLDivElement>(null)

  const handleChange = (date: unknown, dateString: string | string[]) => {
    onConfirm(date, dateString)
    onClose()
  }

  return (
    <Modal
      title={<WizrText type='sub2'>{title}</WizrText>}
      open={open}
      centered
      footer={null}
      onCancel={onClose}
      destroyOnClose
      zIndex={1100}
      width='min(520px, calc(100vw - 32px))'
    >
      <InlineDateTimeHost ref={hostRef} className='wizr-import-date-inline-host'>
        <StyledDatePicker
          open
          inputReadOnly
          format='YYYY-MM-DD HH:mm:ss'
          showTime={showTimeConfig}
          needConfirm
          value={value}
          disabledDate={disabledDate}
          onChange={handleChange}
          getPopupContainer={() => hostRef.current || document.body}
          popupClassName='wizr-import-date-popup wizr-import-date-inline'
        />
      </InlineDateTimeHost>
    </Modal>
  )
}

const StyledButton = styled(WizrButton)`
  height: 40px !important;
  width: 90px !important;
  border-radius: 20px !important;
  border: 1px solid black !important;
`

const defaultTime = '00:00:00'
const showTimeConfig = { defaultValue: dayjs(defaultTime, 'HH:mm:ss') }

const ImportPastTickets = ({ folderId, onCancel }: any) => {
  const [currentDetails, setCurrentDetails] = useState<any>({ integration_key: '', tool: '' })
  const [activePicker, setActivePicker] = useState<ActivePicker>(null)

  const masterIntegrations = useGetMasterIntegrationList()
  const integrationsForTools = useGetIntegrationList()
  useEffect(() => {
    dispatchMasterIntegrationList()
    // dispatchGetIntegrationToolsForTenants()
  }, [])

  const getIcon = (item: any) => {
    switch (item?.integration_key) {
      case 'zendesk':
        return <ZendeskIcon />
      case 'freshdesk':
        return <Freshdesk />
      case 'salesforce':
        return <Salesforce />
      case 'jira':
        return <JiraIcon />
      case 'sharepoint':
        return <SharepointIcon />
      case 'confluence':
        return <ConfluenceIcon />
      case 'slack':
        return <Slack />
      case 'servicenow':
        return <ServiceNow />
      case 'postgres':
        return <PostgreSQLIcon />
      case 'gemini':
        return <Gemini />
      case 'pinecone':
        return <Pinecone />
      case 'mongodb':
        return <MongoDBIcon />
      case 'smtp':
        return <SendEmailIcon />
      default:
        return null
    }
  }

  const dateFormat = 'YYYY-MM-DD HH:mm:ss'

  const disabledDate = (date: Dayjs): boolean => {
    if (!currentDetails?.start_date) return false
    const startDate = dayjs(currentDetails.start_date, dateFormat).startOf('day')
    return date.isBefore(startDate, 'day')
  }

  const onChangeStartDate = (_date: unknown, dateString: string | string[]) => {
    setCurrentDetails({
      ...currentDetails,
      start_date: Array.isArray(dateString) ? dateString[0] : dateString,
    })
  }

  const onChangeEndDate = (_date: unknown, dateString: string | string[]) => {
    setCurrentDetails({
      ...currentDetails,
      end_date: Array.isArray(dateString) ? dateString[0] : dateString,
    })
  }

  const dropDownConverter = (value: string[]) => {
    // Filter out confluence, slack, and any items with empty/invalid data
    const filteredValues = value?.filter((item: any) =>
      item &&
      item.integration_key &&
      item.display_name &&
      !['confluence', 'slack'].includes(item.integration_key)
    );

    const result =
      filteredValues?.length > 0 &&
      filteredValues?.map((item: any) => ({
        label: (
          <div style={{ alignItems: 'center', display: 'flex', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {getIcon(item)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', lineHeight: 'normal' }}>
              {item.display_name || ''}
            </div>
          </div>
        ),
        value: item.integration_key || '',
      }));
    return result
  }

  const dropDownConverterIntegrations = (value: string[]) => {

    const result =
      value?.length > 0 &&
      value?.map((item: any, key: number) => ({

        label: item?.display_name,


        value: item.integration_id,
      }))
    return result
  }

  // Define ticket statuses for different tools
  const ticketStatusByTool = {
    zendesk: [
      { label: "New", value: "new" },
      { label: "Open", value: "open" },
      { label: "Pending", value: "pending" },
      { label: "Hold", value: "hold" },
      { label: "Solved", value: "solved" },
      { label: "Closed", value: "closed" },
    ],
    jira: [
      { label: "To Do", value: "To Do" },
      { label: "In Progress", value: "In Progress" },
      { label: "Done", value: "Done" },
      { label: "Resolved", value: "Resolved" },
      { label: "Closed", value: "Closed" },
      { label: "Reopened", value: "Reopened" },
      { label: "In Review", value: "In Review" },
      { label: "Blocked", value: "Blocked" },
      { label: "Waiting for support", value: "Waiting for support" }
    ],

    freshdesk: [
      { label: "New", value: "new" },
      { label: "Open", value: "open" },
      { label: "Pending", value: "pending" },
      { label: "Hold", value: "hold" },
      { label: "Solved", value: "closed" },
    ],
    salesforce: [
      { label: "New", value: "new" },
      { label: "Open", value: "open" },
      { label: "Pending", value: "pending" },
      { label: "Hold", value: "hold" },
      { label: "Solved", value: "closed" },
    ],
    servicenow: [
      { label: "New", value: "new" },
      { label: "Open", value: "open" },
      { label: "Awaiting Info", value: "awaiting_info" },
      { label: "Resolved", value: "resolved" },
      { label: "Solved", value: "closed" },
      { label: "Cancelled", value: "cancelled" }
    ]
    // Add other tools as needed
  };

  // Function to get ticket status options based on selected tool
  const getTicketStatusOptions = () => {
    return ticketStatusByTool[currentDetails?.tool as keyof typeof ticketStatusByTool] || [];
  };

  // Update tool selection handler
  const onToolSelection = (value: any, label: any) => {
    dispatchGetIntegrationForTools(value);
    setCurrentDetails({
      ...currentDetails,
      tool: value,
      ticket_status: '' // Reset ticket status when tool changes
    });
  };

  const onImportPastTickets = () => {
    const Payload = {
      tool: currentDetails.tool,
      integration_id: currentDetails.integration_key,
      start_date: currentDetails.start_date,
      end_date: currentDetails.end_date,
      ticket_status: currentDetails?.ticket_status,
      folder_id: folderId
    }
    dispatchImportPastTickets(Payload)
  }

  return (
    <WizrFlexLayout background='white' style={{ gap: '15px', padding: '16px' }}>
      <ImportDatePickerDropdownStyles />
      <InputContainer>
        <WizrText type='body2'>Select Tool</WizrText>
        <StyledDropDown
          value={currentDetails?.tool}
          options={dropDownConverter(masterIntegrations)}
          onChange={(value: any, label: any) => {
            onToolSelection(value, label)
          }}
        />
      </InputContainer>

      <InputContainer>
        <WizrText type='body2'>Select Integrations</WizrText>
        <StyledDropDown
          value={currentDetails?.integration_key}
          options={dropDownConverterIntegrations(integrationsForTools)}
          onChange={(value: any, label: any) => {
            setCurrentDetails({ ...currentDetails, integration_key: value })
          }}
        />
      </InputContainer>
      <WizrFlexLayout
        flexDirection='column'
        background='white'
        style={{ gap: '15px', width: '100%' }}
      >
        <DateFieldContainer>
          <WizrText type='body2'>Start Date</WizrText>
          <StyledDatePicker
            format={dateFormat}
            placeholder='Select start date & time'
            inputReadOnly
            open={false}
            showTime={showTimeConfig}
            value={
              currentDetails?.start_date
                ? dayjs(currentDetails.start_date, dateFormat)
                : null
            }
            onOpenChange={(open) => {
              if (open) setActivePicker('start')
            }}
          />
        </DateFieldContainer>
        <DateFieldContainer>
          <WizrText type='body2'>End Date</WizrText>
          <StyledDatePicker
            format={dateFormat}
            placeholder='Select end date & time'
            inputReadOnly
            open={false}
            showTime={showTimeConfig}
            value={
              currentDetails?.end_date
                ? dayjs(currentDetails.end_date, dateFormat)
                : null
            }
            onOpenChange={(open) => {
              if (open) setActivePicker('end')
            }}
          />
        </DateFieldContainer>
      </WizrFlexLayout>

      <DateTimePickerModal
        open={activePicker === 'start'}
        title='Select start date & time'
        value={
          currentDetails?.start_date
            ? dayjs(currentDetails.start_date, dateFormat)
            : null
        }
        onConfirm={onChangeStartDate}
        onClose={() => setActivePicker(null)}
      />
      <DateTimePickerModal
        open={activePicker === 'end'}
        title='Select end date & time'
        value={
          currentDetails?.end_date
            ? dayjs(currentDetails.end_date, dateFormat)
            : null
        }
        disabledDate={disabledDate}
        onConfirm={onChangeEndDate}
        onClose={() => setActivePicker(null)}
      />

      <InputContainer>
        <WizrText type='body2'>Ticket Status</WizrText>
        <StyledDropDown
          value={currentDetails?.ticket_status}
          options={getTicketStatusOptions()}
          onChange={(value: any, label: any) => {
            setCurrentDetails({ ...currentDetails, ticket_status: value })
          }}
          disabled={!currentDetails?.tool} // Disable if no tool is selected
        />
      </InputContainer>
      <WizrFlexLayout
        justifyContent='flex-end'
        flexDirection='row'
        background='white'
        style={{ padding: '10px', gap: '20px' }}
      >
        <StyledButton type='default' text='Cancel' onClick={onCancel}></StyledButton>
        <StyledButton
          type='primary'
          text={<WizrText type='button'>Import</WizrText>}
          onClick={() => {
            onImportPastTickets()
            onCancel()
          }}
        ></StyledButton>
      </WizrFlexLayout>
    </WizrFlexLayout>
  )
}
export default ImportPastTickets
